import React, { useState } from "react";
import { Image, Modal } from "antd";
import { FileTextOutlined } from "@ant-design/icons";

// Renders a CRM activity's attached images/PDFs in a way that actually
// opens inside the iOS/Android WebView wrapper app, not just a desktop
// browser tab.
//
// Why not a plain `<a target="_blank">` (the old behavior): a bare
// Android WebView / iOS WKWebView has no "open a new tab" concept at all
// unless the native app explicitly implements one (WebChromeClient.
// onCreateWindow on Android, WKUIDelegate on iOS) -- neither exists in
// this app's native wrapper, so target="_blank" links were just silently
// swallowed there. Desktop browsers were never the problem.
//
// - Images: antd's own <Image> with `preview` -- an in-page lightbox, no
//   navigation/new-window involved at all, so it works identically on
//   every platform (and is nicer on desktop too: no tab-switching).
// - PDFs: a bare WebView also has no built-in PDF renderer (unlike a full
//   browser's PDF viewer). Opened in an in-app Modal running Google's
//   embeddable viewer (docs.google.com/viewer) in an iframe -- that's just
//   an ordinary webpage, so it renders inside any WebView the same way
//   any other page does; no native PDF capability is required. This
//   requires the file URL to be publicly fetchable (DO Spaces uploads in
//   this app already use ACL: "public-read" -- see uploadToSpaces.js).
const googleViewerUrl = (fileUrl) =>
  `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;

const AttachmentPreviewList = ({ attachments = [], thumbSize = 64 }) => {
  const [previewFile, setPreviewFile] = useState(null);

  if (!attachments.length) return null;

  return (
    <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 8 }}>
      {attachments.map((file, i) =>
        file.type?.startsWith("image/") ? (
          <Image
            key={i}
            src={file.url}
            alt={file.name}
            width={thumbSize}
            height={thumbSize}
            style={{ objectFit: "cover", borderRadius: 8, border: "1px solid #eee" }}
          />
        ) : file.type?.startsWith("audio/") ? (
          // A plain HTML5 <audio> element plays inline in both a desktop
          // browser and an Android/iOS WebView -- unlike the PDF case below,
          // nothing needs to navigate away or embed a viewer page, so this
          // doesn't hit the WebView new-tab problem described above at all.
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 8,
              border: "1px solid #eee",
              background: "#fff",
            }}
          >
            {/* The browser's native scrubber needs real width to render
                usably -- at ~220px Chrome squeezes it down to a sliver next
                to the time/volume controls, making it impossible to seek. */}
            <audio controls preload="none" src={file.url} style={{ height: 32, width: 300 }} />
          </div>
        ) : (
          <button
            key={i}
            type="button"
            onClick={() => setPreviewFile(file)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 10px",
              borderRadius: 8,
              border: "1px solid #eee",
              background: "#fff",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            <FileTextOutlined /> {file.name}
          </button>
        )
      )}

      <Modal
        open={!!previewFile}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        width="min(900px, 94vw)"
        title={previewFile?.name}
        destroyOnClose
      >
        {previewFile && (
          <>
            <iframe
              title={previewFile.name}
              src={googleViewerUrl(previewFile.url)}
              style={{ width: "100%", height: "75vh", border: "none" }}
            />
            {/* Desktop convenience only -- a plain target="_blank" link is a
                no-op inside the WebView wrapper app (no new-tab concept
                there), so this is additive, never the only way to view it. */}
            <a href={previewFile.url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginTop: 8, fontSize: 12 }}>
              Open original
            </a>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AttachmentPreviewList;
